import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@artstorefronts/ui';
import { UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface AccountSelectorProps {
  integrations: any[];
  loading: boolean;
  selectedUserIntegration: any;
  onSelect: (userIntegration: any) => void;
}

export function AccountSelector({ integrations, loading, selectedUserIntegration, onSelect }: AccountSelectorProps) {
  const { t } = useTranslation('social');
  const navigate = useNavigate();

  // Flatten all userIntegrations from all integrations
  const allUserIntegrations = integrations.flatMap((integration: any) =>
    integration.userIntegrations.map((ui: any) => ({
      ...ui,
      integrationName: integration.name,
    }))
  );

  // Auto-select if only one option
  useEffect(() => {
    if (!selectedUserIntegration && allUserIntegrations.length === 1) {
      onSelect(allUserIntegrations[0]);
    }
  }, [allUserIntegrations, selectedUserIntegration, onSelect]);

  if (loading) {
    return <div className="text-sm text-gray-500">{t('account.loading-accounts')}</div>;
  }

  if (allUserIntegrations.length === 0) {
    return (
      <div className="space-y-2">
        <div className="text-sm text-gray-500">{t('account.no-accounts-connected')}</div>
        <Button size="sm" onClick={() => navigate('/integrations')}>
          {t('account.connect-account')}
        </Button>
      </div>
    );
  }

  return (
    <Select
      value={selectedUserIntegration?.id || ''}
      onValueChange={(value) => {
        const selected = allUserIntegrations.find((ui) => ui.id === value);
        onSelect(selected || null);
      }}
    >
      <SelectTrigger className="w-full">
        <SelectValue placeholder={t('account.choose-account')} />
      </SelectTrigger>
      <SelectContent>
        {allUserIntegrations.map((ui: any) => (
          <SelectItem key={ui.id} value={ui.id}>
            <div className="flex items-center gap-2">
              {ui.metadata?.profilePictureUrl ? (
                <img src={ui.metadata.profilePictureUrl} alt={ui.metadata?.username} className="w-5 h-5 rounded-full" />
              ) : (
                <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                  <UserIcon className="w-3 h-3 text-gray-400" />
                </div>
              )}
              @{ui.metadata?.username || t('account.unknown')}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
